import React, { useState } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { CronJobStatus, HTTPMethod } from '../../../../models/CronJob';
import type { CreateCronJobModalProps, FormData } from './types';
import { CronJobType } from './types';

const CreateCronJobModal: React.FC<CreateCronJobModalProps> = ({ show, onHide, onSubmit, isSubmitting = false }) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    cronExpression: '0 0 * * *', // Daily at midnight
    isOneTime: false,
    scheduledDateTime: '',
    status: CronJobStatus.DISABLED,
    cronJobType: CronJobType.HTTP,
    url: '',
    method: HTTPMethod.GET,
    headers: {},
    body: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [headersText, setHeadersText] = useState('');

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors['name'] = 'CronJob name is required';
    }

    if (formData.isOneTime) {
      if (!formData.scheduledDateTime.trim()) {
        newErrors['scheduledDateTime'] = 'Scheduled date and time (UTC) is required for one-time jobs';
      }
    } else {
      if (!formData.cronExpression.trim()) {
        newErrors['cronExpression'] = 'Cron expression is required';
      }
    }

    if (formData.cronJobType === CronJobType.HTTP && !formData.url.trim()) {
      newErrors['url'] = 'URL is required';
    }

    // Validate headers JSON if provided and cronJobType is HTTP
    if (formData.cronJobType === CronJobType.HTTP && headersText.trim()) {
      try {
        JSON.parse(headersText);
      } catch {
        newErrors['headers'] = 'Headers must be valid JSON';
      }
    }

    // Validate body JSON if provided and method is not GET and cronJobType is HTTP
    if (formData.cronJobType === CronJobType.HTTP && formData.body.trim() && formData.method !== HTTPMethod.GET) {
      try {
        JSON.parse(formData.body);
      } catch {
        newErrors['body'] = 'Body must be valid JSON';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };

      // Set default UTC time when switching to one-time mode
      if (field === 'isOneTime' && value === true && !prev.scheduledDateTime) {
        const utcNow = new Date();
        const year = utcNow.getUTCFullYear();
        const month = String(utcNow.getUTCMonth() + 1).padStart(2, '0');
        const day = String(utcNow.getUTCDate()).padStart(2, '0');
        const hours = String(utcNow.getUTCHours() + 1).padStart(2, '0'); // 1 hour from now
        const minutes = String(utcNow.getUTCMinutes()).padStart(2, '0');
        newData.scheduledDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
      }

      return newData;
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleHeadersChange = (value: string) => {
    setHeadersText(value);
    if (errors['headers']) {
      setErrors((prev) => ({ ...prev, headers: '' }));
    }
  };

  const handleReset = () => {
    setFormData({
      name: '',
      cronExpression: '0 0 * * *',
      isOneTime: false,
      scheduledDateTime: '',
      status: CronJobStatus.DISABLED,
      cronJobType: CronJobType.HTTP,
      url: '',
      method: HTTPMethod.GET,
      headers: {},
      body: '',
    });
    setHeadersText('');
    setErrors({});
  };

  const handleClose = () => {
    handleReset();
    onHide();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      let parsedHeaders = {};
      if (headersText.trim()) {
        try {
          parsedHeaders = JSON.parse(headersText);
        } catch {
          // This should not happen due to validation
          return;
        }
      }

      const submitData = {
        ...formData,
        headers: parsedHeaders,
      };

      onSubmit(submitData);
      handleReset();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Create Cron Job</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-4">
            <Form.Label>CronJob Type*</Form.Label>
            <Form.Select
              value={formData.cronJobType}
              onChange={(e) => handleInputChange('cronJobType', e.target.value as CronJobType)}
            >
              <option value={CronJobType.HTTP}>HTTP</option>
            </Form.Select>
          </Form.Group>

          {formData.cronJobType === CronJobType.HTTP && (
            <Alert variant="info">
              <strong>HTTP CronJob:</strong> This will create a scheduled HTTP request that runs according to the cron
              expression.
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>CronJob Name*</Form.Label>
            <Form.Control
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={errors['name'] ? 'is-invalid' : ''}
              placeholder="Enter cron job name"
            />
            {errors['name'] && <div className="invalid-feedback">{errors['name']}</div>}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="one-time-switch"
              label="One Time Execution"
              checked={formData.isOneTime}
              onChange={(e) => handleInputChange('isOneTime', e.target.checked)}
            />
            <Form.Text className="text-muted">
              Enable this to schedule a one-time execution instead of recurring
            </Form.Text>
          </Form.Group>

          {formData.isOneTime ? (
            <Form.Group className="mb-3">
              <Form.Label>Scheduled Date and Time (UTC)*</Form.Label>
              <Form.Control
                type="datetime-local"
                value={formData.scheduledDateTime}
                onChange={(e) => handleInputChange('scheduledDateTime', e.target.value)}
                className={errors['scheduledDateTime'] ? 'is-invalid' : ''}
                min={(() => {
                  // Set min to current UTC time to prevent past time selection
                  const utcNow = new Date();
                  const year = utcNow.getUTCFullYear();
                  const month = String(utcNow.getUTCMonth() + 1).padStart(2, '0');
                  const day = String(utcNow.getUTCDate()).padStart(2, '0');
                  const hours = String(utcNow.getUTCHours()).padStart(2, '0');
                  const minutes = String(utcNow.getUTCMinutes()).padStart(2, '0');
                  return `${year}-${month}-${day}T${hours}:${minutes}`;
                })()}
                placeholder="YYYY-MM-DDTHH:MM"
              />
              <Form.Text className="text-muted">
                Enter UTC time. Current UTC time:{' '}
                {(() => {
                  const utcNow = new Date();
                  const year = utcNow.getUTCFullYear();
                  const month = String(utcNow.getUTCMonth() + 1).padStart(2, '0');
                  const day = String(utcNow.getUTCDate()).padStart(2, '0');
                  const hours = String(utcNow.getUTCHours()).padStart(2, '0');
                  const minutes = String(utcNow.getUTCMinutes()).padStart(2, '0');
                  return `${year}-${month}-${day} at ${hours}:${minutes}`;
                })()}
              </Form.Text>
              {errors['scheduledDateTime'] && <div className="invalid-feedback">{errors['scheduledDateTime']}</div>}
            </Form.Group>
          ) : (
            <Form.Group className="mb-3">
              <Form.Label>Cron Expression*</Form.Label>
              <Form.Control
                type="text"
                value={formData.cronExpression}
                onChange={(e) => handleInputChange('cronExpression', e.target.value)}
                className={errors['cronExpression'] ? 'is-invalid' : ''}
                placeholder="0 0 * * *"
              />
              <Form.Text className="text-muted">
                Format: minute hour day month weekday (e.g., "0 0 * * *" = daily at midnight)
              </Form.Text>
              {errors['cronExpression'] && <div className="invalid-feedback">{errors['cronExpression']}</div>}
            </Form.Group>
          )}

          {formData.cronJobType === CronJobType.HTTP && (
            <>
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>HTTP Method*</Form.Label>
                    <Form.Select
                      value={formData.method}
                      onChange={(e) => handleInputChange('method', e.target.value as HTTPMethod)}
                    >
                      <option value={HTTPMethod.GET}>GET</option>
                      <option value={HTTPMethod.POST}>POST</option>
                      <option value={HTTPMethod.PUT}>PUT</option>
                      <option value={HTTPMethod.DELETE}>DELETE</option>
                      <option value={HTTPMethod.PATCH}>PATCH</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={8}>
                  <Form.Group>
                    <Form.Label>URL*</Form.Label>
                    <Form.Control
                      type="url"
                      value={formData.url}
                      onChange={(e) => handleInputChange('url', e.target.value)}
                      className={errors['url'] ? 'is-invalid' : ''}
                      placeholder="https://example.com/api/endpoint"
                    />
                    {errors['url'] && <div className="invalid-feedback">{errors['url']}</div>}
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>HTTP Headers (JSON)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={headersText}
                  onChange={(e) => handleHeadersChange(e.target.value)}
                  className={errors['headers'] ? 'is-invalid' : ''}
                  placeholder='{"Content-Type": "application/json", "Authorization": "Bearer token"}'
                />
                <Form.Text className="text-muted">Optional. Enter headers as JSON object.</Form.Text>
                {errors['headers'] && <div className="invalid-feedback">{errors['headers']}</div>}
              </Form.Group>

              {formData.method !== HTTPMethod.GET && (
                <Form.Group className="mb-3">
                  <Form.Label>Request Body (JSON)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={formData.body}
                    onChange={(e) => handleInputChange('body', e.target.value)}
                    className={errors['body'] ? 'is-invalid' : ''}
                    placeholder='{"key": "value"}'
                  />
                  <Form.Text className="text-muted">Optional. Enter request body as JSON.</Form.Text>
                  {errors['body'] && <div className="invalid-feedback">{errors['body']}</div>}
                </Form.Group>
              )}
            </>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={formData.status}
              onChange={(e) => handleInputChange('status', e.target.value as CronJobStatus)}
            >
              <option value={CronJobStatus.DISABLED}>Disabled</option>
              <option value={CronJobStatus.ENABLED}>Enabled</option>
            </Form.Select>
            <Form.Text className="text-muted">Disabled cron jobs will not run automatically.</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create CronJob'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateCronJobModal;
